import { createContext, useCallback, useContext, useEffect, useRef, ReactNode } from 'react';
import { useFeedbackPrompt } from '@/hooks/useFeedbackPrompt';
import { FeedbackPromptDialog } from '@/components/FeedbackPromptDialog';
import { usePromptQueue, PromptPriority } from '@/contexts/PromptQueueContext';

export const FEEDBACK_SHOWN_SESSION_KEY = 'grounded_feedback_shown_session';

type WinMomentListener = () => void;

interface FeedbackPromptContextType {
  trackAction: () => Promise<void>;
  /**
   * Subscribe to "user just completed a win moment" events (fires immediately
   * after trackAction's mutation resolves). Returns an unsubscribe fn.
   *
   * Deliberately uses a subscription model — do NOT watch engagement/query
   * data for changes to infer win moments. That approach fires phantom events
   * on load and cache invalidation.
   */
  subscribeWinMoment: (listener: WinMomentListener) => () => void;
}

const noopUnsubscribe = () => {};

const FeedbackPromptContext = createContext<FeedbackPromptContextType | null>(null);

export function useFeedbackTracker() {
  const context = useContext(FeedbackPromptContext);
  if (!context) {
    // No-op fallback when used outside the provider (e.g. unauthenticated).
    return {
      trackAction: async () => {},
      subscribeWinMoment: (_: WinMomentListener) => noopUnsubscribe,
    };
  }
  return context;
}

interface FeedbackPromptProviderProps {
  children: ReactNode;
}

export function FeedbackPromptProvider({ children }: FeedbackPromptProviderProps) {
  const { shouldShowPrompt, trackAction, dismissPrompt, markFeedbackGiven } = useFeedbackPrompt();
  const { requestPrompt, releasePrompt, isActivePrompt } = usePromptQueue();

  // Subscription registry for post-trackAction win-moment listeners.
  const listenersRef = useRef<Set<WinMomentListener>>(new Set());

  const subscribeWinMoment = useCallback((listener: WinMomentListener) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  // Wrap trackAction: await original, then fan out to listeners. Any listener
  // that throws is isolated so it can't interfere with the engagement mutation
  // or with other listeners.
  const wrappedTrackAction = useCallback(async () => {
    await trackAction();
    listenersRef.current.forEach((fn) => {
      try {
        fn();
      } catch (err) {
        console.error('[FeedbackPromptProvider] win-moment listener threw:', err);
      }
    });
  }, [trackAction]);

  // Register with queue when hook says it's time to show
  useEffect(() => {
    if (shouldShowPrompt) {
      requestPrompt('feedback', PromptPriority.FEEDBACK);
    } else {
      releasePrompt('feedback');
    }
  }, [shouldShowPrompt, requestPrompt, releasePrompt]);

  // Mark the session as "feedback shown" the moment the dialog occupies the
  // slot. Read by the App Store review prompt to prevent stacking in the same
  // session across the queue's 2s gap.
  const feedbackActive = isActivePrompt('feedback');
  useEffect(() => {
    if (feedbackActive && typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(FEEDBACK_SHOWN_SESSION_KEY, 'true');
    }
  }, [feedbackActive]);

  const handleDismiss = () => {
    releasePrompt('feedback');
    dismissPrompt();
  };

  const handleFeedbackGiven = () => {
    releasePrompt('feedback');
    markFeedbackGiven();
  };

  return (
    <FeedbackPromptContext.Provider value={{ trackAction: wrappedTrackAction, subscribeWinMoment }}>
      {children}
      <FeedbackPromptDialog
        open={feedbackActive}
        onDismiss={handleDismiss}
        onFeedbackGiven={handleFeedbackGiven}
      />
    </FeedbackPromptContext.Provider>
  );
}
